/*
Used to store metadata external to the object its targeting.
For example, storing release notes separately from a prompt so that the notes can be changed
  w/o the need to create a new version of an immutable object (like a prompt).
*/

import {
  createConfig,
  getConfigByTypeKey,
  getConfigs,
  updateConfig,
  deleteConfig,
} from "./configurationService";
import { currentUserHasRole } from "../services/userService";

const TYPE = "external-metadata";

const viewScope = [
  "owner",
  "admin",
  "prompt-engineer",
  "clinical-author",
  "demonstrator",
  "viewer",
  "champion",
];
const editScope = [...viewScope];
const deleteScope = ["owner", "admin", "prompt-engineer", "clinical-author"];

function deduplicateData(data) {
  const uniqueMap = new Map();
  data.forEach((item) => {
    const uniqueKey = `${item.type}|${item.key}`;
    if (uniqueMap.has(uniqueKey)) {
      const existingItem = uniqueMap.get(uniqueKey);
      if (new Date(item.updated_at) > new Date(existingItem.updated_at)) {
        uniqueMap.set(uniqueKey, item);
      }
    } else {
      uniqueMap.set(uniqueKey, item);
    }
  });
  return Array.from(uniqueMap.values());
}

export const externalMetadataService = {
  getMetadata: async (key, overrideType = null, throw404 = false) => {
    try {
      const result = await getConfigByTypeKey(overrideType || TYPE, key);
      return JSON.parse(result.value);
    } catch (e) {
      if (throw404 && e.response && e.response.status === 404) {
        throw e;
      }
      return null;
    }
  },

  getMetadataList: async (
    keys = null,
    overrideType = null,
    exactMatch = true,
    start = null,
    end = null,
    includeWrapperData = false,
    throw404 = false,
  ) => {
    try {
      const result = await getConfigs(
        overrideType || TYPE,
        keys,
        exactMatch,
        start,
        end,
      );
      const deduped = deduplicateData(result);
      const mapped = {};
      deduped.forEach((item) => {
        mapped[item.key] = JSON.parse(item.value);
        if (includeWrapperData) {
          mapped[item.key].__id = item.id;
          mapped[item.key].__created_at = item.created_at;
          mapped[item.key].__created_by = item.created_by;
          mapped[item.key].__updated_at = item.updated_at;
          mapped[item.key].__updated_by = item.updated_by;
        }
      });
      return mapped;
    } catch (e) {
      if (throw404 && e.response && e.response.status === 404) {
        throw e;
      }
      return null;
    }
  },

  updateMetadata: async (key, externalMetadata, overrideType = null) => {
    const md = await getConfigByTypeKey(overrideType || TYPE, key);
    if (md) {
      const isAdmin = await currentUserHasRole("admin");
      return await updateConfig(
        md.id,
        TYPE,
        md.key,
        JSON.stringify(externalMetadata),
        isAdmin ? viewScope : null,
        isAdmin ? editScope : null,
        isAdmin ? deleteScope : null,
      );
    }
  },

  upsertMetadata: async (key, externalMetadata, overrideType = null) => {
    const md = await getConfigByTypeKey(overrideType || TYPE, key);
    if (md) {
      const isAdmin = await currentUserHasRole("admin");
      return await updateConfig(
        md.id,
        TYPE,
        md.key,
        JSON.stringify(externalMetadata),
        isAdmin ? viewScope : null,
        isAdmin ? editScope : null,
        isAdmin ? deleteScope : null,
      );
    }
    // fallback to create
    return await createConfig(
      TYPE,
      key,
      JSON.stringify(externalMetadata),
      null,
      null,
      viewScope,
      editScope,
      deleteScope,
    );
  },

  createMetadata: async (key, externalMetadata, overrideType = null) => {
    return await createConfig(
      overrideType || TYPE,
      key,
      JSON.stringify(externalMetadata),
      null,
      null,
      viewScope,
      editScope,
      deleteScope,
    );
  },

  deleteMetadata: async (key, overrideType = null) => {
    const md = await getConfigByTypeKey(overrideType || TYPE, key);
    return await deleteConfig(md.id);
  },
};

export default externalMetadataService;
